// Telemetria de la web para el Pulso: pantallas vistas y errores de JavaScript.
//
// Va por su propio axios y no por jwtAuthAxios: el interceptor de ese muestra un Swal en cada
// error, y un fallo al mandar telemetria nunca puede llegarle al usuario. Por lo mismo todo va
// envuelto y sin reintentos: perder una tanda no importa.
//
// Se manda en tandas cada FLUSH_MS y al ocultar la pestana (con keepalive, para que alcance a
// salir aunque se cierre). Ver hermes-backend app/controllers/api/v1/telemetry_controller.rb.
import axios from "axios";
import { API_HOST, APP_VERSION } from "app/utils/constants/paths";

const FLUSH_MS = 20000;
const MAX_ERRORS_PER_PAGE = 20;

let screens = [];
let events = [];
let errors = [];
let errorsSent = 0;
const seenErrors = new Set();
let started = false;

function authHeader() {
    const token = localStorage.getItem("token");
    if (!token) return null;
    return token.startsWith("Bearer ") ? token : `Bearer ${token}`;
}

function headers() {
    const h = {
        "Content-Type": "application/json",
        "X-Client": "web",
        "X-App-Version": APP_VERSION,
        "X-Screen": window.location.pathname,
    };
    const auth = authHeader();
    if (auth) h.Authorization = auth;
    return h;
}

function takeBatch() {
    if (!screens.length && !events.length && !errors.length) return null;
    const batch = { screens, events, errors };
    screens = [];
    events = [];
    errors = [];
    return batch;
}

export function flush({ leaving = false } = {}) {
    const batch = takeBatch();
    if (!batch) return;
    try {
        if (leaving && window.fetch) {
            // keepalive deja que el request sobreviva al cierre de la pestana.
            window.fetch(`${API_HOST}/telemetry`, {
                method: "POST",
                headers: headers(),
                body: JSON.stringify(batch),
                keepalive: true,
            }).catch(() => {});
            return;
        }
        axios.post(`${API_HOST}/telemetry`, batch, { headers: headers(), timeout: 15000 }).catch(() => {});
    } catch (e) {
        // nada: la telemetria no puede romper la pagina
    }
}

export function trackScreen(pathname) {
    if (!pathname) return;
    screens.push({ screen: pathname, at: new Date().toISOString() });
    if (screens.length >= 50) flush();
}

export function trackEvent(name, props = {}) {
    events.push({ name, props, screen: window.location.pathname, at: new Date().toISOString() });
}

// Ruido del navegador que no dice nada de Haulers.
const IGNORED = [/ResizeObserver loop/i, /^Script error\.?$/i, /Non-Error promise rejection captured/i];

export function reportError(error, { fatal = false, context = {} } = {}) {
    try {
        const message = (error && (error.message || String(error))) || "Error sin mensaje";
        if (IGNORED.some((re) => re.test(message))) return;
        if (errorsSent >= MAX_ERRORS_PER_PAGE) return;

        // El mismo error en la misma pantalla cuenta una vez por carga de pagina.
        const key = `${message}|${window.location.pathname}`;
        if (seenErrors.has(key)) return;
        seenErrors.add(key);
        errorsSent += 1;

        errors.push({
            name: (error && error.name) || "Error",
            message: String(message).slice(0, 2000),
            stack: String((error && error.stack) || "").slice(0, 8000),
            screen: window.location.pathname,
            fatal,
            context: { url: window.location.href, userAgent: navigator.userAgent, ...context },
            at: new Date().toISOString(),
        });
        // Un error se manda enseguida: si la pagina se cayo, puede que no haya otra tanda.
        flush();
    } catch (e) {
        // nada
    }
}

export function startTelemetry() {
    if (started) return;
    started = true;

    window.addEventListener("error", (event) => {
        reportError(event.error || { message: event.message, stack: `${event.filename}:${event.lineno}:${event.colno}` }, { fatal: true });
    });
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event.reason;
        // Los rechazos de axios ya los ve el backend (y el usuario, con el Swal): no son de JS.
        if (reason && reason.isAxiosError) return;
        reportError(reason instanceof Error ? reason : { message: String(reason) });
    });
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flush({ leaving: true });
    });
    setInterval(() => flush(), FLUSH_MS);
}
